import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../../from-server/asin-central/model";
import { searchPorAsinsNoLimit } from "../../app/api";

export interface FetchAllPagesArgs {
  readonly queryString: string;
  readonly signal: AbortSignal;
  readonly maxAsins: number;
  readonly timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 30_000;

function createAbortError(message: string): Error {
  const err = new Error(message);
  err.name = "AbortError";
  return err;
}

function hasAsin(
  row: FullAsinWithAudits | RestrictedAsin
): row is FullAsinWithAudits {
  const asin = (row as { asin?: unknown }).asin;
  return typeof asin === "string" && asin.length > 0;
}

export interface FetchAllPagesResult {
  readonly rows: FullAsinWithAudits[];
  // True when the underlying pager or the local filter hit the `maxAsins`
  // cap. Distinct from "the server has more restricted rows" — that
  // case does not set this flag, so callers can surface a genuine
  // truncation warning without false positives on RestrictedAsin rows.
  readonly truncated: boolean;
}

export async function fetchAllPagesForCurrentQuery(
  args: FetchAllPagesArgs
): Promise<FetchAllPagesResult> {
  const {
    queryString,
    signal,
    maxAsins,
    timeoutMs = DEFAULT_TIMEOUT_MS,
  } = args;

  if (signal.aborted) {
    throw createAbortError("Aborted");
  }

  // A private controller mirrors the caller's signal AND the local
  // timeout, and is the one actually passed into the network layer.
  // Aborting it cancels the in-flight axios request(s) — Promise.race
  // alone would not.
  const controller = new AbortController();
  let timedOut = false;

  const onExternalAbort = () => controller.abort();
  signal.addEventListener("abort", onExternalAbort, { once: true });

  const timeoutHandle = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    const rows = await searchPorAsinsNoLimit(queryString, {
      signal: controller.signal,
      maxRows: maxAsins,
    });

    if (controller.signal.aborted) {
      throw createAbortError(timedOut ? "Timeout" : "Aborted");
    }

    const filtered: FullAsinWithAudits[] = [];
    let filterTruncated = false;
    for (const row of rows) {
      if (hasAsin(row)) {
        filtered.push(row);
        if (filtered.length >= maxAsins) {
          filterTruncated = true;
          break;
        }
      }
    }
    // `searchPorAsinsNoLimit` stops paging once it has accumulated
    // `maxRows` raw rows, so a raw count at the cap signals that
    // more results existed on the server.
    const pagerTruncated = rows.length >= maxAsins;
    return { rows: filtered, truncated: pagerTruncated || filterTruncated };
  } catch (err) {
    // Normalize axios cancellations to an AbortError so callers only
    // have to inspect `.name`.
    if (controller.signal.aborted) {
      throw createAbortError(timedOut ? "Timeout" : "Aborted");
    }
    throw err;
  } finally {
    clearTimeout(timeoutHandle);
    signal.removeEventListener("abort", onExternalAbort);
  }
}
